/**
 * API endpoints for Dashboards data
 */
export enum APIEndpoints {
    /** Survey builder embed url endpoint */
    SURVEY_BUILDER = '/dashboard/builder',
    /** Sigma dashboard filters endpoint */
    SIGMA_DASHBOARD_FILTERS = '/template/:dashboard/filters',
    /** Sigma dashboard dynamic options endpoint (portal API) */
    DYNAMIC_OPTIONS = '/dashboards/dynamic-options',
    /** Get template information by dashboard code */
    GET_TEMPLATE = '/template/:dashboardCode',
    /** Get dashboard by code */
    GET_DASHBOARD = '/dashboards/:dashboardCode',
    /** Get Users for Admin Dashboard */
    GET_USERS = '/admin/users',
}

export const CUSTOM_DASHBOARD_SLUG = 'custom-dashboards';

export const DISPLAY_STYLE_CONVERSIONS: Record<string, string> = {
    intensity_scale: 'intensity-scale',
    text_input: 'text-input',
} as const;

export type DisplayStyleKey = keyof typeof DISPLAY_STYLE_CONVERSIONS;
export type DisplayStyleValue = (typeof DISPLAY_STYLE_CONVERSIONS)[DisplayStyleKey];

export const CUSTOM_FILTER_DISPLAY_NAME = ['Name', 'Property', 'Category'];

export const SIGMA_IFRAME_BASE = 'https://app.sigmacomputing.com';

export enum BehaviorTypeEnum {
    Web = 'Web',
    App = 'App',
    Venue = 'Venue',
}

export const ALL_BEHAVIOR_TYPES = [
    BehaviorTypeEnum.Web,
    BehaviorTypeEnum.App,
    BehaviorTypeEnum.Venue,
] as const;

export type BehaviorType = (typeof ALL_BEHAVIOR_TYPES)[number];

export enum FilterGrouping {
    BRAND = 'Brand',
    CATEGORY = 'Category',
}
export enum FilterOptionType {
    CATEGORY = 'category',
    NAME = 'name',
    BEHAVIOR_TYPE = 'behavior-type',
    GROUPING = 'grouping',
}

export enum FilterNames {
    DATE_PERIOD = 'Date-Period',
    DATE_RANGE = 'Date-Range',
    PERIOD_BEGINNING = 'Period-Beginning',
    REGION_FILTER = 'Region-Filter',
    STATE_FILTER = 'State-Filter',
    OS_FILTER = 'OS-Name-Filter',
}

export const CATEGORY_NAME_TEMPLATES = {
    app: 'App',
    app_preview: 'App',
    location: 'Venue',
    location_preview: 'Venue',
    website: 'Website',
    website_preview: 'Website',
    top_ranked_apps: 'App',
    top_ranked_venues: 'Venue',
    top_ranked_websites: 'Website',
    website_journeys: 'Website',
};

export const TOP_N_LABEL = {
    app: 'Top N Apps by Reach',
    app_preview: 'Top N Apps by Reach',
    location: 'Top N Venue Chains by Reach',
    location_preview: 'Top N Venue Chains by Reach',
    website: 'Top N Websites by Reach',
    website_preview: 'Top N Websites by Reach',
    top_ranked_apps: 'Top Apps',
    top_ranked_websites: 'Top Websites',
    top_ranked_venues: 'Top Venues',
    visitor_metrics_center: 'Top N by Reach',
    visitor_metrics_center_syndicated: 'Top N by Reach',
    syndicated_trends_behaviors: 'Top N by Reach',
    visitor_trends: 'Top N Count',
    website_journeys: 'Top Websites',
};

export const DISPLAY_FILTER_TITLE = {
    profiles: 'Make selections to narrow the view.',
    profiles_plus: 'Make selections to narrow the view.',
    website: 'Select below filters to modify visualizations and summary tables.',
    website_preview: 'Select below filters to modify visualizations and summary tables.',
    app: 'Select below filters to modify visualizations and summary tables.',
    app_preview: 'Select below filters to modify visualizations and summary tables.',
    location: 'Select below filters to modify visualizations and summary tables.',
    location_preview: 'Select below filters to modify visualizations and summary tables.',
    consumer_overlap: 'Select below filters to modify visualizations and summary tables.',
    visitor_metrics_center: 'Select below filters to modify visualizations and summary tables.',
    visitor_metrics_center_syndicated:
        'Select below filters to modify visualizations and summary tables.',
    syndicated_trends_behaviors:
        'Select below filters to modify visualizations and summary tables.',
    ooh_attribution: 'Select below filters to modify visualizations and summary tables.',
    surveys_omni_consumer_overlap:
        'Select below filters to modify visualizations and summary tables.',
    surveys_omni_consumer_overlap_syndicated:
        'Select below filters to modify visualizations and summary tables.',
};

export const DEPENDENT_FILTER_BEHAVIOR_TYPES = {
    profiles: ALL_BEHAVIOR_TYPES,
    profiles_plus: ALL_BEHAVIOR_TYPES,
    website: [BehaviorTypeEnum.Web],
    app: [BehaviorTypeEnum.App, BehaviorTypeEnum.Web],
    location: [BehaviorTypeEnum.Venue],
    top_ranked_apps: [BehaviorTypeEnum.App],
    top_ranked_websites: [BehaviorTypeEnum.Web],
    top_ranked_venues: [BehaviorTypeEnum.Venue],
};

/**
 * Dashboard codes that should not show footer
 */
export const NO_FOOTER_DASHBOARDS = [
    'profiles',
    'reach_monitor',
    'consumer_overlap',
    'profiles_plus',
    'visitor_trends',
    'top_ranked_websites',
    'top_ranked_apps',
    'top_ranked_venues',
    'website_journeys',
];

export enum SIGMA_PARAMETERS {
    SURVEY_IDS = 'Survey-ID-Parameter',
    NONCE = 'nonce',
    SESSION_LENGTH = 'session_length',
    MODE = 'mode',
    EXTERNAL_USER_TEAM = 'external_user_team',
    EXTERNAL_USER_ID = 'external_user_id',
    EVAL_CONNECTION_ID = 'eval_connection_id',
    CLIENT_ID = 'client_id',
    EMAIL = 'email',
    SHOW_FOOTER = 'show_footer',
    SIGNATURE = 'signature',
    ACCOUNT_TYPE = 'account_type',
}

export enum SIGMA_TEAM_NAMES {
    PROD = 'Clients',
    DEV = 'UAT Team',
    INTERNAL = 'Mfour Team',
}

export enum SIGMA_ACCOUNT_TYPE {
    PROD = 'ClientAccount',
    DEV = 'TestClientAccount',
}
